import { MonitorSmartphone } from "lucide-react";
import { type FormEvent, useState } from "react";

import { Button } from "@/components/ui";
import { deviceRepository } from "@/repositories/deviceRepository";

import { AuthError } from "./AuthError";

/**
 * Pair this terminal with the shop.
 *
 * Shown instead of the PIN keypad when the device holds no token, because a PIN
 * is only ever accepted from a registered terminal — that is what keeps four
 * digits from being an internet-facing credential.
 *
 * A manager creates the device in Settings and reads the six-digit code out to
 * whoever is setting the till up. The code is single use and expires in fifteen
 * minutes, which is what makes six digits acceptable.
 */
export function PairTerminal({ onPaired }: { onPaired: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paired, setPaired] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (code.length < 6 || busy) return;

    setBusy(true);
    setError(null);
    try {
      const device = await deviceRepository.pair(code);
      setPaired(device.name);
      // Let the confirmation register before swapping the keypad in.
      setTimeout(onPaired, 900);
    } catch (cause) {
      setError(
        cause && typeof cause === "object" && "detail" in cause
          ? String((cause as { detail: string }).detail)
          : "That code is not valid or has expired. Ask a manager for a new one.",
      );
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (paired) {
    return (
      <div className="py-6 text-center" role="status">
        <p className="text-base font-semibold text-ink-900">Terminal registered</p>
        <p className="mt-1 text-sm text-ink-500">{paired}</p>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-5">
      <div className="flex flex-col items-center text-center">
        <span className="mb-3 grid size-12 place-items-center rounded-full bg-canvas" aria-hidden>
          <MonitorSmartphone className="size-6 text-ink-400" strokeWidth={1.75} />
        </span>
        <h2 className="text-base font-semibold text-ink-900">Register this terminal</h2>
        <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-ink-500">
          Ask a manager to add this till in Settings, then enter the six-digit code
          they give you. You only need to do this once.
        </p>
      </div>

      <div>
        <label htmlFor="pairing-code" className="mb-1.5 block text-sm font-medium text-ink-900">
          Pairing code
        </label>
        <input
          id="pairing-code"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="off"
          placeholder="000000"
          className="h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-center font-numeric text-2xl tracking-[0.4em] text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
        />
      </div>

      <AuthError message={error} />

      <Button
        type="submit"
        variant="primary"
        size="pos"
        fullWidth
        loading={busy}
        disabled={code.length < 6}
      >
        Register terminal
      </Button>
    </form>
  );
}
